const DAY_MS = 24 * 60 * 60 * 1000;

function parseIsoDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return null;
  const timestamp = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const date = new Date(timestamp);
  return date.getUTCFullYear() === Number(match[1])
    && date.getUTCMonth() === Number(match[2]) - 1
    && date.getUTCDate() === Number(match[3])
    ? timestamp
    : null;
}

function isoDate(timestamp) {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function bucketBounds(bucket) {
  const from = parseIsoDate(bucket && (bucket.date_from || bucket.date_to));
  const inclusiveTo = parseIsoDate(bucket && (bucket.date_to || bucket.date_from));
  return from === null || inclusiveTo === null
    ? null
    : { from, to: inclusiveTo + DAY_MS };
}

function explicitPeriodBounds(period) {
  const from = parseIsoDate(period && period.date_from);
  const inclusiveTo = parseIsoDate(period && period.date_to);
  return from === null || inclusiveTo === null || inclusiveTo < from
    ? null
    : { from, to: inclusiveTo + DAY_MS };
}

function seriesBounds(series) {
  const bounds = (Array.isArray(series) ? series : []).map(bucketBounds).filter(Boolean);
  if (bounds.length === 0) return null;
  return {
    from: Math.min(...bounds.map(bound => bound.from)),
    to: Math.max(...bounds.map(bound => bound.to))
  };
}

function annualBounds(bounds) {
  if (!bounds) return null;
  const year = new Date(bounds.from).getUTCFullYear();
  return {
    from: Date.UTC(year, 0, 1),
    to: Date.UTC(year + 1, 0, 1)
  };
}

function quarterStart(timestamp) {
  const date = new Date(timestamp);
  return Date.UTC(
    date.getUTCFullYear(),
    Math.floor(date.getUTCMonth() / 3) * 3,
    1
  );
}

function equalQuarterBounds(bounds) {
  if (!bounds || bounds.to <= bounds.from) return bounds;
  const firstQuarter = quarterStart(bounds.from);
  const lastQuarter = quarterStart(bounds.to - 1);
  if (firstQuarter === lastQuarter) return bounds;
  const last = new Date(lastQuarter);
  return {
    from: firstQuarter,
    to: Date.UTC(last.getUTCFullYear(), last.getUTCMonth() + 3, 1)
  };
}

function spansMultipleQuarters(bounds) {
  return Boolean(bounds) && bounds.to > bounds.from
    && quarterStart(bounds.from) !== quarterStart(bounds.to - 1);
}

function monthBucketKey(bucket) {
  const bounds = bucketBounds(bucket);
  if (!bounds) return null;
  const from = new Date(bounds.from);
  const inclusiveTo = new Date(bounds.to - DAY_MS);
  return from.getUTCFullYear() === inclusiveTo.getUTCFullYear()
    && from.getUTCMonth() === inclusiveTo.getUTCMonth()
    ? `${from.getUTCFullYear()}-${from.getUTCMonth()}`
    : null;
}

function isMonthGranularity(series) {
  const buckets = Array.isArray(series) ? series : [];
  const keys = buckets.map(monthBucketKey);
  return keys.length > 0
    && keys.every(Boolean)
    && new Set(keys).size === keys.length
    && buckets.some(bucket => {
      const bounds = bucketBounds(bucket);
      return bounds && bounds.to - bounds.from > DAY_MS;
    });
}

function completeMonthBounds(bounds) {
  if (!bounds) return null;
  const from = new Date(bounds.from);
  const to = new Date(bounds.to - 1);
  return {
    from: Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), 1),
    to: Date.UTC(to.getUTCFullYear(), to.getUTCMonth() + 1, 1)
  };
}

function intersectsPeriod(bucket, bounds) {
  const candidate = bucketBounds(bucket);
  if (!bounds) return true;
  if (!candidate) return false;
  return candidate.from < bounds.to && candidate.to > bounds.from;
}

function filterSeriesToPeriod(series, period) {
  const values = Array.isArray(series) ? series : [];
  const bounds = explicitPeriodBounds(period);
  return bounds ? values.filter(bucket => intersectsPeriod(bucket, bounds)) : values;
}

function calendarMonth(bucket) {
  const bounds = bucketBounds(bucket);
  return bounds ? new Date(bounds.from).getUTCMonth() + 1 : null;
}

function valueX(value, bounds, left, right) {
  if (!bounds || bounds.to <= bounds.from) return (left + right) / 2;
  const ratio = Math.max(0, Math.min(1, (value - bounds.from) / (bounds.to - bounds.from)));
  return left + (ratio * (right - left));
}

function monthIndex(year, month, origin) {
  return ((year - origin.getUTCFullYear()) * 12) + month - origin.getUTCMonth();
}

function calendarMonthX(value, bounds, left, right) {
  if (!bounds || bounds.to <= bounds.from) return (left + right) / 2;
  if (value >= bounds.to) return right;
  if (value <= bounds.from) return left;
  const origin = new Date(bounds.from);
  const end = new Date(bounds.to);
  const date = new Date(value);
  const totalMonths = monthIndex(end.getUTCFullYear(), end.getUTCMonth(), origin);
  if (totalMonths <= 0) return valueX(value, bounds, left, right);
  const index = monthIndex(date.getUTCFullYear(), date.getUTCMonth(), origin);
  const monthFrom = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);
  const monthTo = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
  const fraction = (value - monthFrom) / (monthTo - monthFrom);
  const ratio = Math.max(0, Math.min(1, (index + fraction) / totalMonths));
  return left + (ratio * (right - left));
}

function timestampX(value, bounds, left, right, calendarScale) {
  return calendarScale
    ? calendarMonthX(value, bounds, left, right)
    : valueX(value, bounds, left, right);
}

function bucketX(bucket, bounds, left, right, calendarScale) {
  const candidate = bucketBounds(bucket);
  if (candidate && calendarScale) {
    const date = new Date(candidate.from);
    const monthFrom = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);
    const monthTo = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
    return calendarMonthX(monthFrom + ((monthTo - monthFrom) / 2), bounds, left, right);
  }
  return candidate
    ? timestampX(
      candidate.from + ((candidate.to - candidate.from) / 2),
      bounds,
      left,
      right,
      calendarScale
    )
    : null;
}

function indexedX(index, length, left, right) {
  return length <= 1 ? (left + right) / 2 : left + ((index / (length - 1)) * (right - left));
}

function periodQuarterBands(bounds, left, right, calendarScale = false) {
  if (!bounds) return [];
  const first = new Date(bounds.from);
  let cursor = Date.UTC(
    first.getUTCFullYear(),
    Math.floor(first.getUTCMonth() / 3) * 3,
    1
  );
  const bands = [];
  while (cursor < bounds.to) {
    const date = new Date(cursor);
    const quarterEnd = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 3, 1);
    const intersectionFrom = Math.max(cursor, bounds.from);
    const intersectionTo = Math.min(quarterEnd, bounds.to);
    if (intersectionFrom < intersectionTo) {
      const start = timestampX(intersectionFrom, bounds, left, right, calendarScale);
      const end = timestampX(intersectionTo, bounds, left, right, calendarScale);
      bands.push({
        alternate: bands.length % 2 === 1,
        key: `${date.getUTCFullYear()}-Q${Math.floor(date.getUTCMonth() / 3) + 1}`,
        start,
        width: Math.max(0, end - start)
      });
    }
    cursor = quarterEnd;
  }
  return bands;
}

function annualAxisPoints(bounds, left, right) {
  const year = new Date(bounds.from).getUTCFullYear();
  return Array.from({ length: 12 }, (_, index) => {
    const from = Date.UTC(year, index, 1);
    const to = Date.UTC(year, index + 1, 1);
    const label = `${year}-${String(index + 1).padStart(2, '0')}`;
    return {
      annualMonth: true,
      axisLabelAnchor: 'center',
      bucket: { date_from: isoDate(from), date_to: isoDate(to - DAY_MS), key: label, label },
      quarterBoundary: index % 3 === 0,
      x: calendarMonthX(from + ((to - from) / 2), bounds, left, right)
    };
  });
}

function monthAxisPoints(bounds, bands, left, right, calendarScale) {
  if (!bounds) return [];
  const first = new Date(bounds.from);
  let cursor = Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1);
  const points = [];
  while (cursor < bounds.to) {
    const date = new Date(cursor);
    const nextMonth = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
    const label = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
    points.push({
      axisMonth: true,
      axisLabelAnchor: 'center',
      bucket: { date_from: isoDate(cursor), date_to: isoDate(cursor), key: label, label },
      quarterBoundary: date.getUTCMonth() % 3 === 0,
      x: timestampX(cursor + ((nextMonth - cursor) / 2), bounds, left, right, calendarScale)
    });
    cursor = nextMonth;
  }
  const endpointPoints = [...points];
  bands.forEach(band => {
    const [year, quarterLabel] = band.key.split('-Q');
    const quarter = Number(quarterLabel);
    const month = ((quarter - 1) * 3) + 1;
    const label = `${year}-${String(month).padStart(2, '0')}`;
    if (bands.length > 1 && !endpointPoints.some(point => point.bucket.label === label)) {
      const date = `${label}-01`;
      endpointPoints.push({
        axisMonth: true,
        axisLabelAnchor: 'start',
        bucket: { date_from: date, date_to: date, key: `quarter-boundary:${band.key}`, label },
        domainEndpoint: band.start === left,
        quarterBoundary: true,
        x: band.start
      });
    }
  });
  return endpointPoints.sort((firstPoint, secondPoint) => firstPoint.x - secondPoint.x);
}

function createChartTimeline({
  annual = false,
  comparisonPeriod,
  comparisonSeries,
  currentPeriod,
  currentSeries,
  left,
  right
}) {
  const filteredCurrent = filterSeriesToPeriod(currentSeries, currentPeriod);
  const filteredComparison = filterSeriesToPeriod(comparisonSeries, comparisonPeriod);
  const currentActualBounds = explicitPeriodBounds(currentPeriod) || seriesBounds(filteredCurrent);
  const comparisonActualBounds = explicitPeriodBounds(comparisonPeriod) || seriesBounds(filteredComparison);
  const currentMonthGranularity = isMonthGranularity(filteredCurrent);
  const comparisonMonthGranularity = isMonthGranularity(filteredComparison);
  const displayBounds = annual
    ? annualBounds(currentActualBounds)
    : spansMultipleQuarters(currentActualBounds)
      ? equalQuarterBounds(currentActualBounds)
      : currentMonthGranularity ? completeMonthBounds(currentActualBounds) : currentActualBounds;
  const comparisonDisplayBounds = annual
    ? annualBounds(comparisonActualBounds)
    : spansMultipleQuarters(comparisonActualBounds)
      ? equalQuarterBounds(comparisonActualBounds)
      : comparisonMonthGranularity ? completeMonthBounds(comparisonActualBounds) : comparisonActualBounds;
  const currentCalendarScale = annual || spansMultipleQuarters(currentActualBounds) || currentMonthGranularity;
  const comparisonCalendarScale = annual || spansMultipleQuarters(comparisonActualBounds) || comparisonMonthGranularity;
  const currentPoints = filteredCurrent.map((bucket, index) => ({
    bucket,
    x: bucketX(bucket, displayBounds, left, right, currentCalendarScale)
      ?? indexedX(index, filteredCurrent.length, left, right)
  }));
  const comparisonPoints = filteredComparison.map((bucket, index) => ({
    bucket,
    x: bucketX(bucket, comparisonDisplayBounds, left, right, comparisonCalendarScale)
      ?? indexedX(index, filteredComparison.length, left, right)
  }));
  const bands = periodQuarterBands(displayBounds, left, right, currentCalendarScale);
  const currentAxisPoints = annual && displayBounds
    ? annualAxisPoints(displayBounds, left, right)
    : monthAxisPoints(displayBounds, bands, left, right, currentCalendarScale);
  let buckets;
  if (annual && displayBounds) {
    const currentByMonth = new Map(filteredCurrent.map(bucket => [calendarMonth(bucket), bucket]));
    const comparisonByMonth = new Map(filteredComparison.map(bucket => [calendarMonth(bucket), bucket]));
    buckets = annualAxisPoints(displayBounds, left, right).map((point, index) => ({
      ...point.bucket,
      comparisonBucket: comparisonByMonth.get(index + 1) || null,
      comparisonX: comparisonPoints.find(candidate => candidate.bucket === comparisonByMonth.get(index + 1))?.x ?? point.x,
      currentBucket: currentByMonth.get(index + 1) || null,
      currentX: currentPoints.find(candidate => candidate.bucket === currentByMonth.get(index + 1))?.x ?? point.x
    }));
  } else {
    buckets = Array.from({ length: Math.max(filteredCurrent.length, filteredComparison.length) }, (_, index) => {
      const currentPoint = currentPoints[index] || null;
      const comparisonPoint = comparisonPoints[index] || null;
      const displayBucket = currentPoint?.bucket || comparisonPoint?.bucket;
      return displayBucket ? {
        ...displayBucket,
        comparisonBucket: comparisonPoint?.bucket || null,
        comparisonX: comparisonPoint?.x,
        currentBucket: currentPoint?.bucket || null,
        currentX: currentPoint?.x,
        key: `${currentPoint?.bucket?.key || 'gap'}:${comparisonPoint?.bucket?.key || 'gap'}:${index}`
      } : null;
    }).filter(Boolean);
  }

  return {
    axisPoints: currentAxisPoints,
    bands,
    buckets,
    comparisonPoints,
    currentPoints,
    displayBounds
  };
}

export {
  createChartTimeline,
  equalQuarterBounds,
  filterSeriesToPeriod,
  parseIsoDate,
  periodQuarterBands
};
